"use client";

import { useState, useEffect } from "react";
import { useParams } from "@/hooks";
import { AppDispatch } from "@/store";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  uploadProductImage,
} from "@/store/modules/products/thunks";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { productSchema } from "@/validations";
import { usePermission } from "../usePermission/usePermission";
import { useAuth } from "@/context/AuthContext";
import { resetFetched } from "@/store/modules/products/slice";
import { useApiReady } from "@/hooks/useApiReady";

const useProduct = (onSuccess?: () => void) => {
  const { params, setParams, handlePage, updateParams } = useParams();
  const { per_page, current_page } = params;
  const { hasPermission, loading: loadingPermission } = usePermission();
  const { currency } = useAuth();
  const { isReady: isApiReady } = useApiReady();
  const [searchValue, setSearchValue] = useState<string>(params.search || "");
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  // Estado para garantir carregamento apenas uma vez
  const [productsLoadedOnce, setProductsLoadedOnce] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    getValues,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(productSchema),
    defaultValues: {
      product: {
        name: "",
        description: "",
        category_id: null,
        brand_id: null,
        display_in_agenda: false,
        subscriber_discount: null,
      },
      product_stock: {
        purchase_price: "0",
        sale_price: "0",
        employee_price: "",
        quantity: undefined,
        commission: "0",
        minimum_quantity: undefined,
      },
      // image: null,
    },
  });

  const setFormData = (data: Partial<typeof productSchema.__outputType>) => {
    reset(data);
  };

  // useEffect(() => {
  //     const subscription = watch((values) => {
  //         console.log("Valores do formulário:", values);
  //     });
  //     return () => subscription.unsubscribe();
  // }, [watch]);

  const onSubmit = async (data: typeof productSchema.__outputType) => {
    try {
      let result;
      // Backend espera campos aninhados: product[<field>] e product_stock[<field>]
      const buildStructuredFormData = (
        root: typeof productSchema.__outputType,
        includeImage: boolean,
      ) => {
        const fd = new FormData();
        // Normalização product
        const p = root.product;
        if (p) {
          Object.entries(p).forEach(([k, v]) => {
            if (v === undefined || v === null || v === "") return;
            if (k === "display_in_agenda") {
              fd.append(`product[${k}]`, v ? "1" : "0");
            } else if (k === "subscriber_discount") {
              const val = parseInt(String(v), 10);
              if (!isNaN(val)) fd.append(`product[${k}]`, String(val));
            } else {
              fd.append(`product[${k}]`, String(v));
            }
          });
        }
        // Normalização product_stock
        const ps = root.product_stock;
        if (ps) {
          Object.entries(ps).forEach(([k, v]) => {
            if (v === undefined || v === null || v === "") return;
            if (
              [
                "purchase_price",
                "sale_price",
                "employee_price",
                "commission",
              ].includes(k)
            ) {
              // Preços vêm possivelmente formatados - remover máscara simples
              const numeric = String(v)
                .replace(/[^0-9.,-]/g, "")
                .replace(",", ".");
              fd.append(`product_stock[${k}]`, numeric);
            } else if (["quantity", "minimum_quantity"].includes(k)) {
              fd.append(`product_stock[${k}]`, String(parseInt(String(v), 10)));
            } else {
              fd.append(`product_stock[${k}]`, String(v));
            }
          });
        }
        if (includeImage && pendingImageFile) {
          fd.append("image", pendingImageFile);
        }
        return fd;
      };

      const buildStructuredJson = (root: typeof productSchema.__outputType) => {
        const p = { ...root.product } as any;
        if (p.display_in_agenda !== undefined)
          p.display_in_agenda = !!p.display_in_agenda;
        if (
          p.subscriber_discount !== null &&
          p.subscriber_discount !== undefined
        ) {
          const disc = parseInt(String(p.subscriber_discount), 10);
          p.subscriber_discount = isNaN(disc) ? 0 : disc;
        }
        const ps = { ...root.product_stock } as any;
        [
          "purchase_price",
          "sale_price",
          "employee_price",
          "commission",
        ].forEach((k) => {
          if (ps[k] !== undefined && ps[k] !== null && ps[k] !== "") {
            const cleaned = String(ps[k])
              .replace(/[^0-9.,-]/g, "")
              .replace(",", ".");
            const num = parseFloat(cleaned);
            if (!isNaN(num)) ps[k] = num;
            else delete ps[k];
          }
        });
        ["quantity", "minimum_quantity"].forEach((k) => {
          if (ps[k] !== undefined && ps[k] !== null && ps[k] !== "") {
            const num = parseInt(String(ps[k]), 10);
            ps[k] = isNaN(num) ? null : num;
          }
        });
        return { product: p, product_stock: ps };
      };

      if (productSlug && hasPermission("update product")) {
        if (pendingImageFile) {
          const formData = buildStructuredFormData(data, true);
          result = await dispatch(
            updateProduct({
              slug: productSlug,
              data: formData,
            } as any),
          );
        } else {
          const jsonPayload = buildStructuredJson(data);
          result = await dispatch(
            updateProduct({ slug: productSlug, ...jsonPayload }),
          );
        }
      } else {
        if (hasPermission("create product")) {
          if (pendingImageFile) {
            const formData = buildStructuredFormData(data, true);
            result = await dispatch(createProduct(formData as any));
          } else {
            const jsonPayload = buildStructuredJson(data);
            result = await dispatch(createProduct(jsonPayload));
          }
        }
      }

      if (
        createProduct.fulfilled.match(result) ||
        updateProduct.fulfilled.match(result)
      ) {
        if (onSuccess) onSuccess();
        dispatch(resetFetched());
        await dispatch(fetchProducts({ ...params, limit: 200 } as any));
        reset();
        setProductSlug(null);
        setPendingImageFile(null);
      }
    } catch (error) {
      console.error("Erro ao enviar dados:", error);
    }
  };

  const { products, loading, error, fetched } = useSelector(
    (state: any) => state.products,
  );

  const dispatch: AppDispatch = useDispatch();
  const [productSlug, setProductSlug] = useState<any>();

  useEffect(() => {
    if (!isApiReady) return;
    if (hasPermission("read product")) {
      dispatch(fetchProducts({ ...params, limit: 200 } as any));
    }
  }, [params, isApiReady, dispatch]);

  // Função para garantir carregamento dos produtos na primeira abertura
  const loadProducts = () => {
    if (!productsLoadedOnce) {
      if (!products?.data || products?.data.length === 0) {
        if (hasPermission("read product")) {
          dispatch(fetchProducts({ ...params, limit: 200 } as any));
        }
      }
      setProductsLoadedOnce(true);
    }
  };

  // Debounce para busca
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchValue !== params.search) {
        updateParams({
          search: searchValue,
          current_page: 1,
        });
        setCurrentPage(1);
      }
    }, 500); // 500ms de debounce

    return () => clearTimeout(timeoutId);
  }, [searchValue, updateParams, params.search]);

  const handleDelete = async () => {
    try {
      if (productSlug && hasPermission("delete product")) {
        await dispatch(deleteProduct(productSlug));
        setProductSlug(null);
        // Força refresh após deleção para refletir lista atualizada
        dispatch(resetFetched());
        await dispatch(fetchProducts({ ...params, limit: 200 } as any));
        return true;
      }
    } catch (error) {
      console.error("Error deleting supplier:", error);
    }
  };

  const [currentPage, setCurrentPage] = useState(products?.currentPage ?? 1);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    handlePage(page);
  };

  const totalItems = products?.total ?? 0;
  const itemsPerPage = products?.perPage ?? 10;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / itemsPerPage) : 1;
  const pagesArray = Array.from({ length: totalPages }, (_, i) => i + 1);

  const handleFilterChange = (value: string) => {
    setSearchValue(value);
  };

  useEffect(() => {
    if (products?.currentPage && products.currentPage !== currentPage) {
      setCurrentPage(products.currentPage);
    }
  }, [products?.currentPage]);

  return {
    register,
    handleSubmit,
    onSubmit,
    reset,
    errors,
    isSubmitting,
    products,
    loading,
    error,
    setProductSlug,
    productSlug,
    handleDelete,
    setFormData,
    getValues,
    control,
    handlePage,
    params,
    currentPage,
    pagesArray,
    handlePageChange,
    itemsPerPage,
    totalPages,
    currency,
    handleFilterChange,
    searchValue,
    pendingImageFile,
    setPendingImageFile,
    loadProducts, // expõe função para garantir carregamento
  };
};

export default useProduct;
